import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Check, Shield, Clock, Star, Loader2, CreditCard } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function PricingPage() {
  var stateHooks = {
    plans: useState([]),
    loading: useState(true),
    selectedPlan: useState(null),
    billingCycle: useState('monthly'),
    processing: useState(false)
  };

  var plans = stateHooks.plans[0];
  var setPlans = stateHooks.plans[1];
  var loading = stateHooks.loading[0];
  var setLoading = stateHooks.loading[1];
  var selectedPlan = stateHooks.selectedPlan[0];
  var setSelectedPlan = stateHooks.selectedPlan[1];
  var billingCycle = stateHooks.billingCycle[0];
  var setBillingCycle = stateHooks.billingCycle[1];
  var processing = stateHooks.processing[0];
  var setProcessing = stateHooks.processing[1];

  useEffect(function() {
    fetch(API_URL + '/api/billing/plans')
      .then(function(res) { return res.json(); })
      .then(function(data) {
        setPlans(data.plans || []);
        setLoading(false);
      })
      .catch(function() {
        setLoading(false);
      });
  }, []);

  function handleCheckout() {
    if (!selectedPlan) {
      alert('Please select a plan');
      return;
    }
    setProcessing(true);

    fetch(API_URL + '/api/billing/checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        plan_id: selectedPlan,
        billing_cycle: billingCycle,
        add_ons: [],
        origin_url: window.location.origin
      })
    })
      .then(function(res) { return res.json(); })
      .then(function(data) {
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        }
        setProcessing(false);
      })
      .catch(function() {
        setProcessing(false);
      });
  }

  function selectPlan(planId) {
    setSelectedPlan(planId);
  }

  function getMonthlyPrice(plan) {
    if (billingCycle === 'yearly') {
      return Math.round(plan.yearly_price / 12);
    }
    return plan.monthly_price;
  }

  function renderFeatures(features) {
    var items = [];
    for (var i = 0; i < features.length; i++) {
      items.push(
        <div key={i} className="flex items-center gap-3">
          <Check className="w-5 h-5 text-[#84CC16] flex-shrink-0" />
          <span className="text-[#475569]">{features[i]}</span>
        </div>
      );
    }
    return items;
  }

  function renderPlanCard(plan, index) {
    var isSelected = selectedPlan === plan.id;
    var monthlyPrice = getMonthlyPrice(plan);
    var yearlyTotal = plan.yearly_price;
    var isPopular = index === 1;
    
    return (
      <div
        key={plan.id}
        onClick={function() { selectPlan(plan.id); }}
        className={
          "relative bg-white rounded-2xl p-8 cursor-pointer transition-all duration-300 " +
          (isSelected 
            ? "border-2 border-[#014DB7] shadow-2xl scale-105" 
            : "border border-[#E2E8F0] hover:border-[#014DB7]/50 hover:shadow-xl")
        }
      >
        {isPopular && (
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#014DB7] text-white px-4 py-1 rounded-full text-sm font-semibold">
            Most Popular
          </div>
        )}
        
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-[#0A1628] mb-2">{plan.name}</h3>
          <p className="text-[#64748B]">{plan.description}</p>
        </div>
        
        <div className="text-center mb-8">
          <span className="text-5xl font-bold text-[#0A1628]">${monthlyPrice}</span>
          <span className="text-[#64748B] text-lg">/month</span>
          {billingCycle === 'yearly' && (
            <p className="text-sm text-[#84CC16] font-medium mt-2">Billed ${yearlyTotal}/year (Save 17%)</p>
          )}
        </div>
        
        <div className="space-y-4 mb-8">
          {renderFeatures(plan.features)}
        </div>
        
        <div 
          className={
            "w-full py-3 rounded-xl text-center font-semibold transition-all " + 
            (isSelected 
              ? "bg-[#014DB7] text-white" 
              : "bg-[#F1F5F9] text-[#0A1628] hover:bg-[#014DB7]/10")
          }
        >
          {isSelected ? '✓ Selected' : 'Select Plan'}
        </div>
      </div>
    );
  }

  function renderPlans() {
    var cards = [];
    for (var i = 0; i < plans.length; i++) {
      cards.push(renderPlanCard(plans[i], i));
    }
    return cards;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-48">
        <Loader2 className="w-10 h-10 animate-spin text-[#014DB7]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F8FAFC] to-white pt-40 pb-20">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-[#0A1628] mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-[#64748B] max-w-2xl mx-auto">
            Choose the perfect plan for your DME business. Start with a 14-day free trial.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-12">
          <div className="bg-[#F1F5F9] p-1.5 rounded-xl inline-flex">
            <button
              onClick={function() { setBillingCycle('monthly'); }}
              data-testid="billing-monthly-btn"
              className={
                "px-8 py-3 rounded-lg font-semibold transition-all " + 
                (billingCycle === 'monthly' 
                  ? "bg-white text-[#0A1628] shadow-md" 
                  : "text-[#64748B] hover:text-[#0A1628]")
              }
            >
              Monthly
            </button>
            <button
              onClick={function() { setBillingCycle('yearly'); }}
              data-testid="billing-yearly-btn"
              className={
                "px-8 py-3 rounded-lg font-semibold transition-all " + 
                (billingCycle === 'yearly' 
                  ? "bg-white text-[#0A1628] shadow-md" 
                  : "text-[#64748B] hover:text-[#0A1628]")
              }
            >
              Yearly <span className="text-[#84CC16] ml-1">Save 17%</span>
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {renderPlans()}
        </div>

        {/* CTA Section */}
        {selectedPlan && (
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-8 shadow-lg max-w-2xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <p className="text-[#64748B] mb-1">Ready to get started?</p>
                <p className="text-2xl font-bold text-[#0A1628]">
                  {selectedPlan} Plan Selected
                </p>
              </div>
              <Button 
                onClick={handleCheckout} 
                disabled={processing}
                data-testid="checkout-btn"
                className="bg-[#014DB7] hover:bg-[#003A8C] text-white px-8 py-6 text-lg font-bold rounded-xl"
              >
                <CreditCard className="w-5 h-5 mr-2" />
                {processing ? 'Processing...' : 'Start Free Trial'}
              </Button>
            </div>
          </div>
        )}

        {/* Trust Badges */}
        <div className="flex flex-wrap justify-center gap-8 mt-16 pt-8 border-t border-[#E2E8F0]">
          <div className="flex items-center gap-3 text-[#64748B]">
            <Shield className="w-6 h-6 text-[#014DB7]" />
            <span className="font-medium">HIPAA Compliant</span>
          </div>
          <div className="flex items-center gap-3 text-[#64748B]">
            <Clock className="w-6 h-6 text-[#014DB7]" />
            <span className="font-medium">Cancel Anytime</span>
          </div>
          <div className="flex items-center gap-3 text-[#64748B]">
            <Star className="w-6 h-6 text-[#014DB7]" />
            <span className="font-medium">14-Day Free Trial</span>
          </div>
        </div>

        {/* FAQ Preview */}
        <div className="mt-20 text-center">
          <h2 className="text-2xl font-bold text-[#0A1628] mb-4">Have Questions?</h2>
          <p className="text-[#64748B] mb-6">
            Our team is here to help you choose the right plan for your business.
          </p>
          <a 
            href="/contact" 
            className="text-[#014DB7] font-semibold hover:underline"
          >
            Contact Sales →
          </a>
        </div>
      </div>
    </div>
  );
}

export default PricingPage;
